/**
 * Support chat state for the window (SupportWindow.js): the practice's requests,
 * the one on screen, sending (text and images), read markers, image uploads and
 * downloads, dictation, and the window's own UI state.
 *
 * Wiring: the practice-side API (utils/supportApi.js) through React Query; the
 * gate is useSupportStatus; live refresh rides the call-monitor socket
 * (subscribeSupportEvents: ids only, every event re-reads through the API), with
 * a 10-minute poll as the net under it. The practice only ever travels in the
 * URL; the sender comes from the token on the server.
 *
 * ONE PRACTICE AT A TIME. All state lives in PracticeSupport, keyed by the
 * practice: a switch drops the list, the open request, every draft, the task
 * card and the window state together, so nothing typed or picked for practice A
 * can be sent to practice B. Because of that key this provider wraps only the
 * support window (App.js), never the app: its children remount on a switch.
 * The images fetched for display are object URLs held here, so the same remount
 * revokes all of them.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useAuth } from '../../auth/AuthContext';
import { usePractice } from '../../context/PracticeContext';
import { useCallState } from '../../context/CallStateContext';
import useSupportStatus, { supportStatusKey } from '../../hooks/useSupportStatus';
import {
  listConversations,
  getConversation,
  openConversation,
  sendMessage,
  markRead as putReadMarker,
  setClosed as putClosed,
  uploadAttachment,
  fetchAttachment,
  dictate as postDictation,
  newClientMsgId,
} from '../../utils/supportApi';
import {
  DICTATION_TEXT,
  SUPPORT_TEXT,
  adaptAttachment,
  adaptConversation,
  adaptConversations,
  adaptFeatures,
  appendMessage,
  dictationErrorText,
  errorCode,
  isGoneError,
  isNotActiveError,
  lastMessageOf,
  loadErrorText,
  patchConversation,
  sendErrorText,
  uploadErrorText,
  upsertConversation,
} from './supportAdapter';
import { prepareImage } from './prepareImage';

export const SupportContext = createContext(null);

/** For the window and everything in it. Without the chat: null, never a crash. */
export const useSupport = () => useContext(SupportContext);

/** Only the net under the socket, which reports every change as it happens. */
const POLL_MS = 10 * 60 * 1000;
const NO_ROWS = [];

const listKey = (practiceId) => ['supportConversations', practiceId];
const detailKey = (practiceId, conversationId) => ['supportConversation', practiceId, conversationId];

/**
 * Where the window was (not whether it was open), per USER and practice, so a
 * reload lands there again, and a colleague at the same computer does not land
 * in someone else's request. Without a user id nothing is remembered.
 */
const windowStorageKey = (userId, practiceId) => `deskbot-support-window:${userId}:${practiceId}`;

const readStoredWindow = (userId, practiceId) => {
  try {
    const stored = userId ? JSON.parse(localStorage.getItem(windowStorageKey(userId, practiceId)) || 'null') : null;
    if (stored && stored.view === 'thread' && typeof stored.conversation === 'string' && stored.conversation) {
      return { view: 'thread', conversationId: stored.conversation };
    }
  } catch { /* private mode or broken JSON: start at the beginning */ }
  return { view: 'new', conversationId: null };
};

const writeStoredWindow = (userId, practiceId, view, conversationId) => {
  if (!userId) return;
  try {
    localStorage.setItem(windowStorageKey(userId, practiceId), JSON.stringify({
      view,
      conversation: view === 'thread' ? conversationId : null,
    }));
  } catch { /* without storage it still works, only not across a reload */ }
};

/** The attachment ids as strings, in thumbnail order (the composer keeps to the count limit). */
const idList = (attachmentIds) => (Array.isArray(attachmentIds) ? attachmentIds.filter(Boolean).map(String) : []);

function PracticeSupport({ practiceId, practiceName, userId, serverFeatures, children }) {
  const queryClient = useQueryClient();
  const { subscribeSupportEvents } = useCallState();

  // After a reload the window always starts closed. Open again, the request on
  // screen would count as read before anyone at the practice looked at it, and
  // at the front desk the number on the button is what reports news. Where it
  // was is remembered, so the next click lands there again.
  const [restored] = useState(() => readStoredWindow(userId, practiceId));
  const [windowOpen, setWindowOpen] = useState(false);
  const [view, setView] = useState(restored.view); // 'new' | 'thread'
  const [currentId, setCurrentId] = useState(restored.conversationId);
  const [reference, setReference] = useState(null); // task card of the request being written
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false); // synchronous guard: `sending` lands one render late
  const [goneAt, setGoneAt] = useState(null);
  // client_msg_id per draft, {draftKey -> {signature, id}}: the same draft sent
  // again after a failure reuses its id, so a send whose answer got lost is
  // replayed by the server instead of posted twice. The signature is the text AND
  // the attachment ids: another text or other images is another draft.
  const draftsRef = useRef(new Map());
  // Newest message id each conversation was reported read up to (one report per message).
  const reportedReadRef = useRef(new Map());
  // Images on screen, {attachmentId -> {url, promise}}: one fetch per image
  // however many places show it, and the object URLs to revoke on unmount.
  const imagesRef = useRef(new Map());

  const refreshStatus = useCallback(() => {
    queryClient.invalidateQueries(supportStatusKey(practiceId));
  }, [queryClient, practiceId]);

  const listQuery = useQuery(listKey(practiceId), () => listConversations(practiceId), {
    retry: false,
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: true,
    // In test mode (again), or not for this user in this mode: /status is asked
    // again, and its answer closes the window instead of leaving an error in it.
    onError: (err) => { if (isNotActiveError(err)) refreshStatus(); },
  });

  const detailQuery = useQuery(
    detailKey(practiceId, currentId),
    () => getConversation(practiceId, currentId),
    {
      enabled: windowOpen && view === 'thread' && !!currentId,
      staleTime: 0,
      retry: false,
      onError: () => {},
    },
  );
  const { refetch: refetchCurrent } = detailQuery;

  // Local merge into a cached answer that a read already on its way cannot undo:
  // that read is cancelled first (RQ then restores the state from before it), the
  // change is applied, and the refetch every caller triggers afterwards reconciles.
  const mergeCache = useCallback(async (key, update) => {
    await queryClient.cancelQueries(key, { exact: true });
    const old = queryClient.getQueryData(key);
    if (old !== undefined) queryClient.setQueryData(key, update(old));
  }, [queryClient]);

  /** Re-reads the list and one request (without an id: every request), for the server's truth. */
  const reread = useCallback((conversationId) => {
    queryClient.invalidateQueries(listKey(practiceId));
    queryClient.invalidateQueries(conversationId ? detailKey(practiceId, conversationId) : ['supportConversation', practiceId]);
  }, [queryClient, practiceId]);

  // Live: a message, a read or erledigt in one request re-reads the list and that
  // request; a socket (re)connect re-reads everything. {link: true} is left to
  // useSupportStatus, which re-asks /status; this provider follows its answer.
  useEffect(() => subscribeSupportEvents((data) => {
    if (!data?.link) reread(data?.conversation_id);
  }), [subscribeSupportEvents, reread]);

  // Opening the window re-reads the list, in case the socket missed something.
  useEffect(() => {
    if (windowOpen) queryClient.invalidateQueries(listKey(practiceId));
  }, [windowOpen, queryClient, practiceId]);

  useEffect(() => {
    writeStoredWindow(userId, practiceId, view, currentId);
  }, [userId, practiceId, view, currentId]);

  // Unmount (which a practice switch is, see the key) frees every image. The map
  // is replaced first, so a fetch still on its way finds its entry gone and
  // creates no URL that nothing would revoke.
  useEffect(() => () => {
    const images = imagesRef.current;
    imagesRef.current = new Map();
    images.forEach((entry) => { if (entry.url) URL.revokeObjectURL(entry.url); });
  }, []);

  // A remembered request that no longer exists is no dead end: forget it, back to
  // the start, and say so in one calm sentence (after the list is re-read, whose
  // next successful load clears it again). A real fault (network, access) stays
  // on screen with "Erneut versuchen", because trying again can help there.
  const detailError = detailQuery.error;
  const handledErrorRef = useRef(null);
  useEffect(() => {
    if (!detailError || handledErrorRef.current === detailError) return;
    handledErrorRef.current = detailError;
    if (isNotActiveError(detailError)) {
      refreshStatus();
      return;
    }
    if (!isGoneError(detailError)) return;
    setCurrentId(null);
    setView('new');
    queryClient.invalidateQueries(listKey(practiceId))
      .catch(() => {})
      .then(() => setGoneAt(Date.now()));
  }, [detailError, refreshStatus, queryClient, practiceId]);

  const listRows = listQuery.data || NO_ROWS;
  const conversations = useMemo(() => adaptConversations(listRows), [listRows]);
  const unreadCount = useMemo(() => conversations.filter((c) => c.unread > 0).length, [conversations]);
  const current = useMemo(() => adaptConversation(detailQuery.data), [detailQuery.data]);
  const features = useMemo(() => adaptFeatures(serverFeatures), [serverFeatures]);

  let error = null;
  if (listQuery.isError) error = SUPPORT_TEXT.unreachable;
  else if (goneAt !== null && listQuery.dataUpdatedAt <= goneAt) error = SUPPORT_TEXT.gone;

  const currentError = !detailQuery.data && detailQuery.isError && !isGoneError(detailQuery.error)
    ? loadErrorText(detailQuery.error)
    : null;

  const reload = useCallback(() => { refetchCurrent(); }, [refetchCurrent]);

  const draftId = useCallback((key, body, attachmentIds) => {
    const signature = JSON.stringify([body, attachmentIds]);
    const drafts = draftsRef.current;
    const known = drafts.get(key);
    if (known && known.signature === signature) return known.id;
    const id = newClientMsgId();
    drafts.set(key, { signature, id });
    return id;
  }, []);

  /**
   * What both sends share: one at a time, the draft's client_msg_id and the
   * failure texts. `post(clientMsgId)` sends and merges locally; when it
   * resolves {ok: true}, the draft's id is used up. A message needs text or at
   * least one image.
   */
  const runSend = useCallback(async (key, body, attachmentIds, post) => {
    if ((!body && !attachmentIds.length) || sendingRef.current) return { ok: false };
    sendingRef.current = true;
    setSending(true);
    try {
      const result = await post(draftId(key, body, attachmentIds));
      if (result.ok) draftsRef.current.delete(key);
      return result;
    } catch (err) {
      // That id belongs to another request and can never succeed: the next attempt gets a new one.
      if (errorCode(err) === 'client_msg_id_conflict') draftsRef.current.delete(key);
      if (isNotActiveError(err)) refreshStatus();
      return { ok: false, error: sendErrorText(err) };
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  }, [draftId, refreshStatus]);

  /**
   * Opens a new request with its first message (text, images or both) and the
   * task card, if any. Resolves to {ok, id} or {ok: false, error}.
   */
  const startConversation = useCallback(async (text, attachmentIds) => {
    const body = String(text || '').trim();
    const ids = idList(attachmentIds);
    const taskId = reference ? reference.id : null;
    return runSend(`new:${taskId || ''}`, body, ids, async (clientMsgId) => {
      const res = await openConversation(practiceId, { body, clientMsgId, taskId, attachmentIds: ids });
      const conv = res && res.conversation;
      if (!conv || !conv.conversation_id) return { ok: false, error: SUPPORT_TEXT.notSent };
      const message = res.message && res.message.id ? res.message : null;
      const row = conv.last_message || !message ? conv : { ...conv, last_message: lastMessageOf(message) };
      queryClient.setQueryData(detailKey(practiceId, conv.conversation_id), { ...row, messages: message ? [message] : [] });
      await mergeCache(listKey(practiceId), (rows) => upsertConversation(rows, row));
      queryClient.invalidateQueries(listKey(practiceId));
      setReference(null); // the card belonged to the request just opened
      return { ok: true, id: conv.conversation_id };
    });
  }, [practiceId, reference, queryClient, runSend, mergeCache]);

  /** A follow-up in the request on screen (reopens it if erledigt). Resolves to {ok} or {ok: false, error}. */
  const sendReply = useCallback(async (text, attachmentIds) => {
    const id = currentId;
    const body = String(text || '').trim();
    const ids = idList(attachmentIds);
    if (!id) return { ok: false };
    return runSend(`reply:${id}`, body, ids, async (clientMsgId) => {
      const res = await sendMessage(practiceId, id, { body, clientMsgId, attachmentIds: ids });
      const message = res && res.message;
      if (message && message.id) {
        await Promise.all([
          mergeCache(detailKey(practiceId, id), (detail) => appendMessage(detail, message)),
          mergeCache(listKey(practiceId), (rows) => patchConversation(rows, id, {
            closed: false,
            last_message: lastMessageOf(message),
          })),
        ]);
      }
      reread(id);
      return { ok: true };
    });
  }, [practiceId, currentId, runSend, mergeCache, reread]);

  /**
   * Reports the request read up to its newest loaded message. The window calls it
   * only while it is open, the request is on screen and the tab is visible and
   * focused. The badge resets at once; the server's answer reconciles.
   */
  const markRead = useCallback((conversationId) => {
    const detail = queryClient.getQueryData(detailKey(practiceId, conversationId));
    const messages = detail && Array.isArray(detail.messages) ? detail.messages : [];
    const newest = messages[messages.length - 1];
    if (!newest || !newest.id || reportedReadRef.current.get(conversationId) === newest.id) return;
    reportedReadRef.current.set(conversationId, newest.id);
    // The badges read the list, so that is where the count resets.
    mergeCache(listKey(practiceId), (rows) => patchConversation(rows, conversationId, { unread_count: 0 }));
    putReadMarker(practiceId, conversationId, newest.id).then(
      () => queryClient.invalidateQueries(listKey(practiceId)),
      (err) => {
        // No re-read here: it would bring the count back, the request on screen
        // would report again, and a server that keeps failing would get a PUT and
        // a list read in a loop. The next list read (socket event, opening the
        // window, the poll) brings the truth; from then on it may report again.
        if (reportedReadRef.current.get(conversationId) === newest.id) reportedReadRef.current.delete(conversationId);
        if (isNotActiveError(err)) refreshStatus();
      },
    );
  }, [practiceId, queryClient, mergeCache, refreshStatus]);

  /**
   * Prepares one image (scaled down, metadata gone: prepareImage) and uploads it.
   * Resolves to the attachment {id, width, height, contentType}; rejects with an
   * Error whose message is the German text for the composer.
   */
  const uploadImage = useCallback(async (file) => {
    const { blob } = await prepareImage(file, { maxBytes: features.maxImageBytes || undefined });
    const res = await uploadAttachment(practiceId, blob).catch((err) => {
      if (isNotActiveError(err)) refreshStatus();
      throw new Error(uploadErrorText(err));
    });
    const attachment = adaptAttachment(res && res.attachment);
    if (!attachment) throw new Error(uploadErrorText(null));
    return attachment;
  }, [practiceId, features.maxImageBytes, refreshStatus]);

  /**
   * A recording to text. Resolves to {ok: true, text} or {ok: false, error}; the
   * sound is stored nowhere. The composer appends the text, it never sends it.
   */
  const dictate = useCallback(async (blob) => {
    try {
      const res = await postDictation(practiceId, blob);
      const text = res && typeof res.text === 'string' ? res.text.trim() : '';
      return text ? { ok: true, text } : { ok: false, error: DICTATION_TEXT.noSpeech };
    } catch (err) {
      if (isNotActiveError(err)) refreshStatus();
      return { ok: false, error: dictationErrorText(err) };
    }
  }, [practiceId, refreshStatus]);

  /**
   * The object URL of an attached image, fetched once (with the token; an <img>
   * cannot send it) and shared by everything that shows it. Rejects when the
   * image is not available; a later call tries again.
   */
  const imageUrl = useCallback((attachmentId) => {
    const images = imagesRef.current;
    const known = images.get(attachmentId);
    if (known) return known.promise;
    const entry = { url: null, promise: null };
    entry.promise = fetchAttachment(practiceId, attachmentId).then((blob) => {
      if (imagesRef.current.get(attachmentId) !== entry) throw new Error('image cache closed');
      entry.url = URL.createObjectURL(blob);
      return entry.url;
    });
    entry.promise.catch(() => { if (images.get(attachmentId) === entry) images.delete(attachmentId); });
    images.set(attachmentId, entry);
    return entry.promise;
  }, [practiceId]);

  /** The URL of an image fetched before, at once (no flash of the spinner); otherwise null. */
  const cachedImageUrl = useCallback((attachmentId) => imagesRef.current.get(attachmentId)?.url || null, []);

  /** "Als erledigt markieren" / "Wieder öffnen". Quiet on failure; the refetch shows the truth. */
  const setClosed = useCallback(async (conversationId, closed) => {
    try {
      const conv = await putClosed(practiceId, conversationId, closed);
      if (conv && conv.conversation_id) {
        await Promise.all([
          mergeCache(detailKey(practiceId, conversationId), (d) => ({ ...d, ...conv })),
          mergeCache(listKey(practiceId), (rows) => patchConversation(rows, conversationId, conv)),
        ]);
      }
    } catch (err) {
      if (isNotActiveError(err)) refreshStatus();
    }
    reread(conversationId);
  }, [practiceId, mergeCache, refreshStatus, reread]);

  /**
   * Opens the window: with a request ({conversation}), with a new request
   * ({isNew, reference}) or as it was ({}).
   */
  const showWindow = useCallback(({ conversation = null, isNew = false, reference: card = null } = {}) => {
    setWindowOpen(true);
    if (conversation) {
      setView('thread');
      setCurrentId(conversation);
      return;
    }
    if (isNew) {
      setCurrentId(null);
      setReference(card);
      setView('new');
    }
  }, []);
  const collapse = useCallback(() => setWindowOpen(false), []);

  const loaded = listQuery.isFetched;
  const value = useMemo(() => ({
    practiceId,
    practiceName,
    features,
    conversations,
    loaded,
    error,
    current,
    currentId,
    currentError,
    reload,
    sending,
    unreadCount,
    windowOpen,
    view,
    reference,
    startConversation,
    sendReply,
    markRead,
    setClosed,
    uploadImage,
    dictate,
    imageUrl,
    cachedImageUrl,
    showWindow,
    collapse,
  }), [
    practiceId, practiceName, features, conversations, loaded, error, current, currentId, currentError,
    reload, sending, unreadCount, windowOpen, view, reference,
    startConversation, sendReply, markRead, setClosed, uploadImage, dictate, imageUrl, cachedImageUrl,
    showWindow, collapse,
  ]);

  return <SupportContext.Provider value={value}>{children}</SupportContext.Provider>;
}

/**
 * Mount once, around the support window only (see the note at the top). The
 * value is null unless the chat is on for this user and the practice on screen.
 */
export default function SupportProvider({ children }) {
  const { authenticated, keycloak } = useAuth();
  const { practiceId, currentPractice } = usePractice();
  const status = useSupportStatus(authenticated ? practiceId : null);
  // The Keycloak subject, as the Tasks page scopes its view settings; only for the remembered view.
  const userId = keycloak?.subject || keycloak?.tokenParsed?.sub || null;

  // Off: no provider at all, so useSupport() reads the context's default, null.
  if (!authenticated || !practiceId || !status.enabled) return children;
  return (
    <PracticeSupport
      key={practiceId}
      practiceId={practiceId}
      practiceName={currentPractice?.practice_name || ''}
      userId={userId}
      serverFeatures={status.features}
    >
      {children}
    </PracticeSupport>
  );
}
